import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const JobEditModal = ({ job, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    type: '',
    status: '',
    description: '',
    requirements: '',
    salary: '',
    experience: ''
  });

  const [isSaving, setIsSaving] = useState(false);

  const departmentOptions = [
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Product', label: 'Product' },
    { value: 'Design', label: 'Design' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Sales', label: 'Sales' },
    { value: 'HR', label: 'Human Resources' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Operations', label: 'Operations' }
  ];

  const locationOptions = [
    { value: 'Remote', label: 'Remote' },
    { value: 'New York, NY', label: 'New York, NY' },
    { value: 'San Francisco, CA', label: 'San Francisco, CA' },
    { value: 'Austin, TX', label: 'Austin, TX' },
    { value: 'Seattle, WA', label: 'Seattle, WA' },
    { value: 'Boston, MA', label: 'Boston, MA' },
    { value: 'Chicago, IL', label: 'Chicago, IL' }
  ];

  const typeOptions = [
    { value: 'Full-time', label: 'Full-time' },
    { value: 'Part-time', label: 'Part-time' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Internship', label: 'Internship' }
  ];

  const statusOptions = [
    { value: 'Draft', label: 'Draft' },
    { value: 'Active', label: 'Active' },
    { value: 'Closed', label: 'Closed' }
  ];

  const experienceOptions = [
    { value: 'Entry Level', label: 'Entry Level (0-2 years)' },
    { value: 'Mid Level', label: 'Mid Level (3-5 years)' },
    { value: 'Senior Level', label: 'Senior Level (6-10 years)' },
    { value: 'Executive', label: 'Executive (10+ years)' }
  ];

  useEffect(() => {
    if (job) {
      setFormData({
        title: job?.title || '',
        department: job?.department || '',
        location: job?.location || '',
        type: job?.type || '',
        status: job?.status || '',
        description: job?.description || '',
        requirements: job?.requirements || '',
        salary: job?.salary || '',
        experience: job?.experience || ''
      });
    } else {
      setFormData({
        title: '',
        department: '',
        location: '',
        type: '',
        status: 'Draft',
        description: '',
        requirements: '',
        salary: '',
        experience: ''
      });
    }
  }, [job]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving job:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {job ? 'Edit Job Posting' : 'Create New Job Posting'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="h-8 w-8 p-0"
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <Input
                label="Job Title"
                type="text"
                placeholder="e.g. Senior Software Engineer"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                required
              />

              <Select
                label="Department"
                options={departmentOptions}
                value={formData?.department}
                onChange={(value) => handleInputChange('department', value)}
                placeholder="Select department"
                required
              />

              <Select
                label="Location"
                options={locationOptions}
                value={formData?.location}
                onChange={(value) => handleInputChange('location', value)}
                placeholder="Select location"
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Job Type"
                  options={typeOptions}
                  value={formData?.type}
                  onChange={(value) => handleInputChange('type', value)}
                  placeholder="Select type"
                  required
                />

                <Select
                  label="Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => handleInputChange('status', value)}
                  placeholder="Select status"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Salary Range"
                  type="text"
                  placeholder="e.g. $80,000 - $120,000"
                  value={formData?.salary}
                  onChange={(e) => handleInputChange('salary', e?.target?.value)}
                />

                <Select
                  label="Experience Level"
                  options={experienceOptions}
                  value={formData?.experience}
                  onChange={(value) => handleInputChange('experience', value)}
                  placeholder="Select experience level"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Job Description
                </label>
                <textarea
                  className="w-full h-32 px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  placeholder="Describe the role, responsibilities, and what makes this position exciting..."
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Requirements
                </label>
                <textarea
                  className="w-full h-32 px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  placeholder="List the required skills, qualifications, and experience..."
                  value={formData?.requirements}
                  onChange={(e) => handleInputChange('requirements', e?.target?.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Save" size={14} />
            <span>Changes are auto-saved as draft</span>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="default"
              loading={isSaving}
              onClick={handleSave}
              iconName="Save"
              iconPosition="left"
            >
              {job ? 'Update Job' : 'Create Job'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobEditModal;